import { useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import { StatusBadge } from '../../../../src/components/primitives/BadgePresets';
import { Box } from '../../../../src/components/primitives/Box';
import { Button } from '../../../../src/components/primitives/Button';
import { ListRow } from '../../../../src/components/primitives/ListRow';
import { HStack, VStack } from '../../../../src/components/primitives/Stack';
import { Text } from '../../../../src/components/primitives/Text';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useModal } from '../../../../src/contexts/ModalContext';
import { useTranslation } from '../../../../src/contexts/TranslationContext';
import { LeadSourceSecretModal } from '../../../../src/features/lead-sources/components/LeadSourceSecretModal';
import {
  buildIntakeCredentialValue,
  canManageLeadSourceConnections,
  consumePendingLeadSourceSecret,
  type IntakeCredentialValue,
} from '../../../../src/features/lead-sources/utils';
import { useCurrentOrgRole } from '../../../../src/hooks/useAuthOrgRole';
import {
  useDeleteLeadSourceConnection,
  useLeadSourceConnection,
  useRevokeLeadSourceConnection,
  useRotateLeadSourceCredential,
} from '../../../../src/hooks/useLeadSources';
import type { LeadSourceConnectionStatus } from '../../../../src/lib/leadSourcesApi';
import { colors } from '../../../../src/theme/colors';
import { tokens } from '../../../../src/theme/tokens';

const API_BASE_URL = process.env.EXPO_PUBLIC_API_URL || 'http://localhost:3000';

const STATUS_TO_BADGE: Record<LeadSourceConnectionStatus, 'success' | 'warning' | 'danger'> = {
  active: 'success',
  paused: 'warning',
  archived: 'danger',
};

export default function LeadSourceDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const router = useRouter();
  const { currentOrganization } = useAuth();
  const { confirm } = useModal();
  const role = useCurrentOrgRole();
  const canManage = canManageLeadSourceConnections(role);
  const organizationId = currentOrganization?.id;

  const { data: connection, isLoading } = useLeadSourceConnection(organizationId, id);
  const { mutateAsync: rotateCredential } = useRotateLeadSourceCredential();
  const { mutateAsync: revokeConnection } = useRevokeLeadSourceConnection();
  const { mutateAsync: deleteConnection } = useDeleteLeadSourceConnection();

  // Creation always issues a path_token credential (see lead-sources.tsx).
  // Read as a lazy useState initializer — not an effect — so the secret
  // modal is already `visible` on the very first render instead of flipping
  // open a tick after mount; it is consumed here exactly once and never
  // persisted anywhere else.
  const [revealedCredential, setRevealedCredential] = useState<IntakeCredentialValue | null>(() => {
    if (!id) return null;
    const pending = consumePendingLeadSourceSecret(id);
    return pending
      ? buildIntakeCredentialValue({
          apiBaseUrl: API_BASE_URL,
          authMode: 'path_token',
          plaintext: pending.plaintext,
        })
      : null;
  });

  const handleRotate = async () => {
    if (!connection) return;
    const authMode = connection.credentials[0]?.authMode ?? 'path_token';
    const issued = await rotateCredential(connection.id);
    setRevealedCredential(
      buildIntakeCredentialValue({
        apiBaseUrl: API_BASE_URL,
        authMode,
        plaintext: issued.plaintext,
      }),
    );
  };

  const handleRevoke = async () => {
    if (!connection) return;
    const confirmed = await confirm({
      title: t('leadSources.confirmRevoke.title'),
      message: t('leadSources.confirmRevoke.message'),
      variant: 'destructive',
    });
    if (confirmed) await revokeConnection(connection.id);
  };

  const handleDelete = async () => {
    if (!connection) return;
    const confirmed = await confirm({
      title: t('leadSources.confirmDelete.title'),
      message: t('leadSources.confirmDelete.message'),
      variant: 'destructive',
    });
    if (confirmed) {
      await deleteConnection(connection.id);
      router.back();
    }
  };

  if (isLoading || !connection) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator color={colors.primary.neon} />
      </Box>
    );
  }

  return (
    <ScrollView testID="lead_source_detail_screen" contentContainerStyle={styles.content}>
      <VStack spacing="xs">
        <Text variant="h4" color={colors.text.primary} accessibilityRole="header">
          {connection.name}
        </Text>
        <StatusBadge status={STATUS_TO_BADGE[connection.status]}>
          {t(`leadSources.status.${connection.status}`)}
        </StatusBadge>
      </VStack>

      {!canManage && (
        <Box padding="md" backgroundColor={colors.status.info.bg} borderRadius="md">
          <Text variant="caption" color={colors.text.secondary}>
            {t('leadSources.readOnlyNotice')}
          </Text>
        </Box>
      )}

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.primary}>
          {t('leadSources.detail.credentialsTitle')}
        </Text>
        {connection.credentials.map((credential) => (
          <ListRow
            key={credential.id}
            title={credential.keyId}
            subtitle={
              credential.revokedAt ? 'revoked' : credential.expiresAt ? 'rotating' : 'active'
            }
          />
        ))}
        {canManage && (
          <Button
            variant="outline"
            onPress={handleRotate}
            accessibilityLabel={t('leadSources.actions.rotate')}
          >
            <Text variant="body" color={colors.primary[500]}>
              {t('leadSources.actions.rotate')}
            </Text>
          </Button>
        )}
      </VStack>

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.primary}>
          {t('leadSources.detail.mappingTitle')}
        </Text>
        {connection.fieldMapping.length > 0 ? (
          connection.fieldMapping.map((mapping) => (
            <ListRow key={mapping.header} title={mapping.header} subtitle={mapping.crmField} />
          ))
        ) : (
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leadSources.empty.connectedDescription')}
          </Text>
        )}
      </VStack>

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.primary}>
          {t('leadSources.detail.errorsTitle')}
        </Text>
        {connection.lastErrorCode ? (
          <VStack spacing="xxs">
            <Text variant="body" color={colors.status.error.main}>
              {t('leadSources.detail.lastErrorCode')}: {connection.lastErrorCode}
            </Text>
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leadSources.detail.lastErrorAt')}: {connection.lastErrorAt}
            </Text>
          </VStack>
        ) : (
          <Text variant="caption" color={colors.text.tertiary}>
            {t('leadSources.detail.noErrors')}
          </Text>
        )}
      </VStack>

      {canManage && (
        <HStack spacing="md">
          <Button
            variant="outline"
            onPress={handleRevoke}
            accessibilityLabel={t('leadSources.actions.revoke')}
          >
            <Text variant="body" color={colors.status.warning.main}>
              {t('leadSources.actions.revoke')}
            </Text>
          </Button>
          <Button
            variant="ghost"
            onPress={handleDelete}
            accessibilityLabel={t('leadSources.actions.delete')}
          >
            <Text variant="body" color={colors.status.error.main}>
              {t('leadSources.actions.delete')}
            </Text>
          </Button>
        </HStack>
      )}

      <LeadSourceSecretModal
        visible={!!revealedCredential}
        credential={revealedCredential}
        onDismiss={() => setRevealedCredential(null)}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing.xl,
  },
});
